import { debounce } from 'throttle-debounce';

import { $config } from '@client/core/atoms/config.js';
import { updateMetricByKey } from '@client/core/atoms/metrics.js';
import {
  $placementsMap,
  updatePlacementKeyValueById
} from '@client/core/atoms/placements.js';
import {
  fetchAllElements,
  fetchElement,
  fetchElementById
} from '@client/core/utils/dom/domUtils.js';
import {
  Brand,
  DeviceType,
  DisplayPlacementId,
  type PlacementId,
  PlacementStatus
} from '@schibsted-nmp/advertising-shared';

const FILTER_LIST_ID = 'advertising--filters-bottom-marker';
const MARGIN = 100;
const DEBOUNCE_MS = 200;
const HEIGHT_CHANGE_THRESHOLD = 10;
const ELEMENT_CHECK_INTERVAL_MS = 100;
const MAX_ELEMENT_CHECK_ATTEMPTS = 100;

const fetchFilterListElement = () => fetchElementById(FILTER_LIST_ID);

type AdvtComponentElement = HTMLElement & { placementId?: string };

function getPlacementIdFromElement(el: Element): PlacementId | null {
  const fromAttribute = el.getAttribute('placementid');
  if (fromAttribute) return fromAttribute as PlacementId;

  if ('placementId' in el) {
    return (el as AdvtComponentElement).placementId as PlacementId | null;
  }

  return null;
}

function isOverlapping(
  adElement: Element,
  filterListElement: Element
): boolean {
  const filterBottomPlacement =
    filterListElement.getBoundingClientRect().bottom + window.scrollY + MARGIN;

  const elementTop = adElement.getBoundingClientRect().top + window.scrollY;

  return elementTop < filterBottomPlacement;
}

export function isBannerOverlappingFilters(placementId: PlacementId) {
  const config = $config.get();
  if (config?.brand !== Brand.Dba) return false;
  if (config.deviceType !== DeviceType.Desktop) return false;

  const filterListElement = fetchFilterListElement();
  if (!filterListElement) return false;

  let adElement = fetchElement(`advt-component[placementid="${placementId}"]`);

  if (!adElement) {
    const allAdvtComponents = fetchAllElements('advt-component');
    adElement =
      Array.from(allAdvtComponents).find(
        (el) => getPlacementIdFromElement(el) === placementId
      ) ?? null;
  }

  if (!adElement) return false;

  return isOverlapping(adElement, filterListElement);
}

export function checkBannerCollisions() {
  const config = $config.get();
  if (config?.brand !== Brand.Dba) return false;
  if (config.deviceType !== DeviceType.Desktop) return false;

  const filterListElement = fetchFilterListElement();
  if (!filterListElement) return false;

  const placementsMap = $placementsMap.get();

  const middleElements = fetchAllElements(
    'advt-component[placementid^="advt_middle_"]'
  );
  const bottomElements = fetchAllElements(
    `advt-component[placementid="${DisplayPlacementId.AdvtBottom1}"]`
  );
  const intermingleElements = fetchAllElements(
    'advt-component.intermingles-ad'
  );

  const allCollapsibleElements = [
    ...middleElements,
    ...bottomElements,
    ...intermingleElements
  ];

  if (allCollapsibleElements.length === 0) return false;

  let hasOverlap = false;

  for (const adElement of allCollapsibleElements) {
    const placementId = getPlacementIdFromElement(adElement);

    if (placementId) {
      const placement = placementsMap[placementId];
      if (!placement || placement.status !== 'loaded') continue;
    }

    if (!isOverlapping(adElement, filterListElement)) continue;

    if (placementId) {
      updateMetricByKey(placementId, PlacementStatus.CollapsedOnCollision);
      updatePlacementKeyValueById(
        placementId,
        'status',
        'collapsedOnCollision'
      );
    } else {
      // Fallback for elements without placementId (shouldn't happen normally,
      // but as a safety net we directly toggle CSS classes to hide the ad,
      // since we can't update the atom without knowing which placement to update)
      adElement.classList.add('advt-empty');
      adElement.classList.remove('advt-loaded');
    }

    hasOverlap = true;
  }

  return hasOverlap;
}

let filterObserverInstance: ResizeObserver | null = null;
let elementCheckIntervalId: ReturnType<typeof setInterval> | null = null;
let debouncedCheckFunction: ReturnType<typeof debounce> | null = null;
let lastKnownFilterHeight = 0;
let isSettingUp = false;
let unsubscribePlacements: (() => void) | null = null;
const checkedPlacements = new Set<string>();

function isCollapsiblePlacementId(id: string) {
  // Note: Intermingle placements use advt_middle_ prefix (e.g., advt_middle_1),
  // so they are covered by the first condition
  return (
    id.startsWith('advt_middle_') ||
    id === String(DisplayPlacementId.AdvtBottom1)
  );
}

/**
 * Schedules collision checks with multiple retries to handle layout timing issues.
 *
 * On page load with pre-selected filters, the browser may not have completed layout
 * when ads finish loading. Element positions from getBoundingClientRect() can be
 * incorrect until layout stabilizes. The retry strategy:
 *
 * 1. Double requestAnimationFrame - ensures at least one paint cycle has occurred
 * 2. 500ms retry - catches cases where layout settles shortly after initial check
 * 3. 1000ms retry - final fallback for slow-loading pages or complex layouts
 */
function scheduleCollisionCheck() {
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      const hasOverlap = checkBannerCollisions();
      if (!hasOverlap) {
        setTimeout(() => {
          const hasOverlapRetry = checkBannerCollisions();
          if (!hasOverlapRetry) {
            setTimeout(() => checkBannerCollisions(), 1000);
          }
        }, 500);
      }
    });
  });
}

function observePlacementLoads() {
  if (unsubscribePlacements) return;

  unsubscribePlacements = $placementsMap.subscribe((placements) => {
    let hasNewlyLoadedPlacements = false;

    for (const [id, placement] of Object.entries(placements)) {
      if (!isCollapsiblePlacementId(id)) continue;

      if (placement.status !== 'loaded') {
        checkedPlacements.delete(id);
        continue;
      }

      if (checkedPlacements.has(id)) continue;

      checkedPlacements.add(id);
      hasNewlyLoadedPlacements = true;
    }

    if (hasNewlyLoadedPlacements) {
      scheduleCollisionCheck();
    }
  });
}

function setupFilterResizeObserver(filterContainer: Element) {
  if (filterObserverInstance) {
    filterObserverInstance.disconnect();
    filterObserverInstance = null;
  }

  if (debouncedCheckFunction) {
    debouncedCheckFunction.cancel();
    debouncedCheckFunction = null;
  }

  lastKnownFilterHeight = filterContainer.getBoundingClientRect().height;

  debouncedCheckFunction = debounce(DEBOUNCE_MS, () => {
    const currentHeight = filterContainer.getBoundingClientRect().height;

    if (
      Math.abs(currentHeight - lastKnownFilterHeight) >= HEIGHT_CHANGE_THRESHOLD
    ) {
      lastKnownFilterHeight = currentHeight;
      checkBannerCollisions();
    }
  });

  filterObserverInstance = new ResizeObserver(debouncedCheckFunction);
  filterObserverInstance.observe(filterContainer);
  scheduleCollisionCheck();
}

/**
 * Observes the filter container for size changes and checks for ad overlap.
 * This handles cases where filters expand dynamically (e.g., "See all" clicks)
 * that may not trigger the EXPANDABLE_UPDATE event.
 *
 * If the filter marker element is not yet in the DOM, this function will poll
 * for it to appear using setInterval (more efficient than MutationObserver on document.body).
 */
export function observeFilterContainerChanges() {
  if (isSettingUp || filterObserverInstance || elementCheckIntervalId) {
    return;
  }

  if (typeof ResizeObserver === 'undefined') {
    return;
  }

  isSettingUp = true;

  try {
    const filterListElement = fetchFilterListElement();
    if (!filterListElement) {
      let attempts = 0;

      elementCheckIntervalId = setInterval(() => {
        try {
          attempts += 1;

          const element = fetchFilterListElement();
          if (element?.parentElement) {
            if (elementCheckIntervalId) {
              clearInterval(elementCheckIntervalId);
              elementCheckIntervalId = null;
            }
            setupFilterResizeObserver(element.parentElement);
            isSettingUp = false;
          } else if (attempts >= MAX_ELEMENT_CHECK_ATTEMPTS) {
            if (elementCheckIntervalId) {
              clearInterval(elementCheckIntervalId);
              elementCheckIntervalId = null;
            }
            isSettingUp = false;
          }
        } catch {
          if (elementCheckIntervalId) {
            clearInterval(elementCheckIntervalId);
            elementCheckIntervalId = null;
          }
          isSettingUp = false;
        }
      }, ELEMENT_CHECK_INTERVAL_MS);

      return;
    }

    const filterContainer = filterListElement.parentElement;
    if (!filterContainer) {
      isSettingUp = false;
      return;
    }

    setupFilterResizeObserver(filterContainer);
    isSettingUp = false;
  } catch {
    isSettingUp = false;
    if (elementCheckIntervalId) {
      clearInterval(elementCheckIntervalId);
      elementCheckIntervalId = null;
    }
  }
}

export function disconnectFilterObserver() {
  if (filterObserverInstance) {
    filterObserverInstance.disconnect();
    filterObserverInstance = null;
  }

  if (debouncedCheckFunction) {
    debouncedCheckFunction.cancel();
    debouncedCheckFunction = null;
  }

  if (elementCheckIntervalId) {
    clearInterval(elementCheckIntervalId);
    elementCheckIntervalId = null;
  }

  if (unsubscribePlacements) {
    unsubscribePlacements();
    unsubscribePlacements = null;
  }

  checkedPlacements.clear();
  isSettingUp = false;
  lastKnownFilterHeight = 0;
}

let cleanupHandlersRegistered = false;

function registerCleanupHandlers() {
  if (cleanupHandlersRegistered) return;
  cleanupHandlersRegistered = true;

  window.addEventListener('pagehide', disconnectFilterObserver, {
    once: true
  });

  window.addEventListener('beforeunload', disconnectFilterObserver, {
    once: true
  });

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
      disconnectFilterObserver();
    }
  });
}

export function initializeFilterCollisionDetection() {
  const config = $config.get();
  if (config?.brand !== Brand.Dba) return;
  if (config.deviceType !== DeviceType.Desktop) return;

  registerCleanupHandlers();
  observeFilterContainerChanges();
  observePlacementLoads();

  if (document.readyState === 'complete') {
    scheduleCollisionCheck();
  } else {
    window.addEventListener(
      'load',
      () => {
        scheduleCollisionCheck();
      },
      { once: true }
    );
  }
}
